import {
  getOpportunityStatus,
  OpportunityStatus,
} from '@totem/components/ticketing/opportunityStatus';
import { StatusHistory } from '@totem/components/ticketing/optimizeTypes';
import {
  calculateAnnualSavings,
  calculateTotalSavings,
} from '@totem/components/ticketing/ticket/specialCases/utilities';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';

const DAY_MS = 1000 * 60 * 60 * 24;
const VERIFY_WINDOW_DAYS = 30;

// Prefer the full-term total; fall back to the annual figure for records
// whose term can't be computed (missing/zero term), keeping totals
// conservative rather than dropping them.
export const totalOrAnnual = (rec: TicketQueryResult): number => {
  const opt = rec.ticket.extras?.optimize;
  if (!opt) return 0;
  const total = calculateTotalSavings(opt);
  return total > 0 ? total : calculateAnnualSavings(opt);
};

export const isQuickWin = (rec: TicketQueryResult): boolean => {
  const opt = rec.ticket.extras?.optimize;
  return opt?.levelOfEffort === 'Low' && opt?.scopeOfReturn === 'High';
};

// The customer-facing six-state pipeline. Draft and Pending Review are one
// stage (neither has been reviewed yet); Verifying splits into two once 30
// days have passed since the ticket's most recent transition into
// Verifying (statusHistory). Rejected/Deferred/Cancelled sit outside this
// funnel — same as the source report.
export type PipelineBucketKey =
  | 'pendingReview'
  | 'approved'
  | 'inProgress'
  | 'verifying'
  | 'confirm'
  | 'booked';

export type PipelineStage = {
  key: PipelineBucketKey;
  number: string;
  // Short internal name, used in compact contexts (the state strip).
  name: string;
  // Customer-facing label — what the reader sees this stage as.
  label: string;
  owner: string;
  description: string;
};

export const PIPELINE_STAGES: PipelineStage[] = [
  {
    key: 'pendingReview',
    number: '01',
    name: 'Pending review',
    label: 'Awaiting your approval',
    owner: 'You',
    description:
      'Opportunity is open. IB is ready to execute once you sign off.',
  },
  {
    key: 'approved',
    number: '02',
    name: 'Approved',
    label: 'Approved · scheduled',
    owner: 'IB Optimization Engineer',
    description:
      'Your approval is recorded. Work is scheduled into the next maintenance window. No site access required.',
  },
  {
    key: 'inProgress',
    number: '03',
    name: 'In progress',
    label: 'IB is executing now',
    owner: 'IB Optimization Engineer',
    description:
      'Task-level execution is underway. Live status comes from the field — no action needed from you.',
  },
  {
    key: 'verifying',
    number: '04',
    name: 'Verifying',
    label: '30-day verification window',
    owner: 'IB · automated',
    description:
      'The change is live. IB is measuring the result before asking you to confirm the savings.',
  },
  {
    key: 'confirm',
    number: '05',
    name: 'Confirm savings',
    label: 'Verification ready — confirm savings',
    owner: 'You',
    description:
      'The verification window is complete. Savings are ready to recognise against your capital plan.',
  },
  {
    key: 'booked',
    number: '06',
    name: 'Realized',
    label: 'Savings realized',
    owner: 'Auto',
    description:
      'Recognised against the capital plan. Recurring savings continue for the balance of the term.',
  },
];

export const getPipelineBucket = (
  rec: TicketQueryResult,
): PipelineBucketKey | null => {
  const status: OpportunityStatus = getOpportunityStatus(rec);

  switch (status) {
    case 'Draft':
    case 'Pending Review':
      return 'pendingReview';
    case 'Approved':
      return 'approved';
    case 'In Progress':
      return 'inProgress';
    case 'Verifying': {
      const history: StatusHistory[] =
        rec.ticket.extras?.optimize?.statusHistory ?? [];
      const enteredVerifying = [...history]
        .filter((evt) => evt.toStatus === 'Verifying' && evt.changedAt)
        .sort(
          (evtA, evtB) =>
            new Date(evtB.changedAt).getTime() -
            new Date(evtA.changedAt).getTime(),
        )[0];

      if (!enteredVerifying) return 'verifying';

      const daysSince =
        (Date.now() - new Date(enteredVerifying.changedAt).getTime()) / DAY_MS;
      return daysSince >= VERIFY_WINDOW_DAYS ? 'confirm' : 'verifying';
    }
    case 'Savings Booked':
      return 'booked';
    default:
      return null;
  }
};
